import { api } from '../api';
import type {
  AssociationHistoryRow,
  AssociationRow,
  AssociationTypeRow,
  OffsetPaginated,
} from '@/types/domain';

export interface AssociationTypeUsage {
  associationCount: number;
  usedSlotKeys: string[];
}

type RawAssociationTypeSlot = Partial<AssociationTypeRow['slots'][number]> & {
  key?: string;
  min?: number;
  max?: number;
};

type AssociationTypeApiRow = Omit<AssociationTypeRow, 'slots'> & {
  slots?: RawAssociationTypeSlot[];
};

function normalizeAssociationType(row: AssociationTypeApiRow): AssociationTypeRow {
  return {
    ...row,
    slots: (row.slots ?? []).map((slot, index) => ({
      slotKey: slot.slotKey ?? slot.key ?? '',
      label: slot.label ?? slot.slotKey ?? slot.key ?? '',
      entityTypeId: slot.entityTypeId ?? 0,
      minCount: slot.minCount ?? slot.min ?? 1,
      maxCount: slot.maxCount ?? slot.max ?? 1,
      exclusive: slot.exclusive === true,
      position: slot.position ?? index,
    })),
  };
}

export interface AssociationListParams {
  associationTypeId?: number;
  contextId?: number;
  status?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

function buildAssocQuery(params: AssociationListParams): string {
  const search = new URLSearchParams();
  if (params.associationTypeId) search.set('associationTypeId', String(params.associationTypeId));
  if (params.contextId) search.set('contextId', String(params.contextId));
  if (params.status) search.set('status', params.status);
  if (params.q) search.set('q', params.q.slice(0, 256));
  if (params.limit) search.set('limit', String(params.limit));
  if (params.offset) search.set('offset', String(params.offset));
  const qs = search.toString();
  return qs ? `/associations?${qs}` : '/associations';
}

export const associationTypesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listAssociationTypes: builder.query<AssociationTypeRow[], void>({
      query: () => '/association-types',
      transformResponse: (response: AssociationTypeApiRow[]) => response.map(normalizeAssociationType),
      providesTags: [{ type: 'AssociationType', id: 'LIST' }],
    }),
    getAssociationType: builder.query<AssociationTypeRow, number>({
      query: (id) => `/association-types/${id}`,
      transformResponse: (response: AssociationTypeApiRow) => normalizeAssociationType(response),
      providesTags: (_r, _e, id) => [{ type: 'AssociationType', id }],
    }),
    getAssociationTypeUsage: builder.query<AssociationTypeUsage, number>({
      query: (id) => `/association-types/${id}/usage`,
      providesTags: (_r, _e, id) => [{ type: 'AssociationType', id: `usage-${id}` }],
    }),
    createAssociationType: builder.mutation<AssociationTypeRow, Record<string, unknown>>({
      query: (body) => ({ url: '/association-types', method: 'POST', body }),
      invalidatesTags: [{ type: 'AssociationType', id: 'LIST' }],
    }),
    updateAssociationType: builder.mutation<AssociationTypeRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/association-types/${id}`, method: 'PATCH', body }),
      transformResponse: (response: AssociationTypeApiRow) => normalizeAssociationType(response),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AssociationType', id }, { type: 'AssociationType', id: 'LIST' }],
    }),
    updateAssociationTypeSlots: builder.mutation<
      AssociationTypeRow,
      { id: number; slots: AssociationTypeRow['slots'] }
    >({
      query: ({ id, slots }) => ({
        url: `/association-types/${id}/slots`,
        method: 'PATCH',
        body: {
          slots: slots.map((slot) => ({
            slotKey: slot.slotKey,
            label: slot.label,
            entityTypeId: slot.entityTypeId,
            min: slot.minCount,
            max: slot.maxCount,
            exclusive: slot.exclusive,
          })),
        },
      }),
      transformResponse: (response: AssociationTypeApiRow) => normalizeAssociationType(response),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'AssociationType', id },
        { type: 'AssociationType', id: 'LIST' },
        { type: 'AssociationType', id: `usage-${id}` },
      ],
    }),
    deleteAssociationType: builder.mutation<void, number>({
      query: (id) => ({ url: `/association-types/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'AssociationType', id: 'LIST' }],
    }),
  }),
});

export const associationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listAssociations: builder.query<OffsetPaginated<AssociationRow>, AssociationListParams>({
      query: (params) => buildAssocQuery(params),
      providesTags: (result) => [
        ...(result?.items ?? []).map(({ id }) => ({ type: 'Association' as const, id })),
        { type: 'Association', id: 'LIST' },
      ],
    }),
    getAssociation: builder.query<AssociationRow, number>({
      query: (id) => `/associations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Association', id }],
    }),
    createAssociation: builder.mutation<AssociationRow, Record<string, unknown>>({
      query: (body) => ({ url: '/associations', method: 'POST', body }),
      invalidatesTags: [{ type: 'Association', id: 'LIST' }],
    }),
    updateAssociation: builder.mutation<AssociationRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/associations/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Association', id }, { type: 'Association', id: 'LIST' }],
    }),
    addAssociationMember: builder.mutation<unknown, { id: number; slotKey: string; entityId: number }>({
      query: ({ id, slotKey, entityId }) => ({
        url: `/associations/${id}/members`,
        method: 'POST',
        body: { slotKey, entityId },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Association', id }],
    }),
    removeAssociationMember: builder.mutation<void, { id: number; memberId: number }>({
      query: ({ id, memberId }) => ({ url: `/associations/${id}/members/${memberId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Association', id }],
    }),
    closeAssociation: builder.mutation<AssociationRow, number>({
      query: (id) => ({ url: `/associations/${id}/close`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Association', id }, { type: 'Association', id: 'LIST' }],
    }),
    getAssociationHistory: builder.query<AssociationHistoryRow[], number>({
      query: (id) => `/associations/${id}/history`,
      providesTags: (_r, _e, id) => [{ type: 'Association', id: `history-${id}` }],
    }),
    assignAssociationProfile: builder.mutation<
      { associationId: number; profileId: number },
      { associationId: number; profileId: number }
    >({
      query: ({ associationId, profileId }) => ({
        url: `/associations/${associationId}/requirement-profiles`,
        method: 'POST',
        body: { profileId },
      }),
      invalidatesTags: (_r, _e, { associationId }) => [{ type: 'Association', id: associationId }],
    }),
    unassignAssociationProfile: builder.mutation<
      void,
      { associationId: number; profileId: number }
    >({
      query: ({ associationId, profileId }) => ({
        url: `/associations/${associationId}/requirement-profiles/${profileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { associationId }) => [{ type: 'Association', id: associationId }],
    }),
    getAssociationCompliance: builder.query<Record<string, unknown>, number>({
      query: (id) => `/associations/${id}/compliance`,
      providesTags: (_r, _e, id) => [{ type: 'Compliance', id }],
    }),
    evaluateAssociationCompliance: builder.mutation<{ queued: boolean }, number>({
      query: (id) => ({ url: `/associations/${id}/compliance/evaluate`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Compliance', id }, { type: 'Association', id }],
    }),
    swapAssociationMember: builder.mutation<
      unknown,
      { associationId: number; memberId: number; entityId: number }
    >({
      query: ({ associationId, memberId, entityId }) => ({
        url: `/associations/${associationId}/members/${memberId}/swap`,
        method: 'PATCH',
        body: { entityId },
      }),
      invalidatesTags: (_r, _e, { associationId }) => [
        { type: 'Association', id: associationId },
        { type: 'Compliance', id: `OPERATIONS_DETAIL_${associationId}` },
      ],
    }),
    getDocumentationMatrix: builder.query<{
      associationId: number;
      summary: { vigentes: number; porVencer: number; vencidos: number; pendientes: number; faltantes: number };
      rows: Array<{
        key: string;
        slotKey: string;
        slotLabel: string;
        memberId: number;
        entityId: number;
        documentTypeId: number;
        documentTypeName: string;
        state: string;
        documentId: number | null;
        sourceProfiles: Array<{ id: number; name: string; contextName: string | null }>;
      }>;
    }, number>({
      query: (id) => `/associations/${id}/documentation-matrix`,
      providesTags: (_r, _e, id) => [{ type: 'Association', id: `matrix-${id}` }],
    }),
  }),
});

export const {
  useListAssociationTypesQuery,
  useGetAssociationTypeQuery,
  useGetAssociationTypeUsageQuery,
  useCreateAssociationTypeMutation,
  useUpdateAssociationTypeMutation,
  useUpdateAssociationTypeSlotsMutation,
  useDeleteAssociationTypeMutation,
} = associationTypesApi;

export const {
  useListAssociationsQuery,
  useGetAssociationQuery,
  useCreateAssociationMutation,
  useUpdateAssociationMutation,
  useAddAssociationMemberMutation,
  useRemoveAssociationMemberMutation,
  useCloseAssociationMutation,
  useGetAssociationHistoryQuery,
  useAssignAssociationProfileMutation,
  useUnassignAssociationProfileMutation,
  useGetAssociationComplianceQuery,
  useEvaluateAssociationComplianceMutation,
  useSwapAssociationMemberMutation,
  useGetDocumentationMatrixQuery,
} = associationsApi;
